type MerchantLike = { id: string; name: string; logoUrl?: string | null };
type WorkspaceLike = { id: string; name: string; logoUrl: string | null; merchants: MerchantLike[] };

export type SidebarLogoData = {
  merchants?: MerchantLike[];
  workspaces?: WorkspaceLike[];
};

export type SidebarLogoSelection = { kind: "logo"; logoUrl: string | null; name: string } | { kind: "portfolio" };

type ResolveArgs = {
  merchantId: string;
  workspaceId?: string;
  isPortfolio: boolean;
  /** Workspace id from `last_accessed`, used to pick the logo on the portfolio overview. */
  lastAccessedWorkspaceId?: string | null;
};

/**
 * Decide which logo the collapsed sidebar should show, based on the active view.
 *
 * Precedence:
 *  1. Viewing a merchant — show that merchant's own logo, even when the URL also
 *     carries a workspace id (workspace-scoped brand routes). This fixes the bug
 *     where the workspace logo was shown instead of the selected merchant's.
 *  2. Workspace selected (explicit in the URL, or the last-accessed workspace when
 *     on the portfolio overview) — show the workspace logo.
 *  3. Portfolio with no resolvable workspace — portfolio placeholder.
 *  4. Nothing matched — generic placeholder.
 */
export function resolveSidebarLogo(
  data: SidebarLogoData | null | undefined,
  { merchantId, workspaceId, isPortfolio, lastAccessedWorkspaceId }: ResolveArgs,
): SidebarLogoSelection {
  const workspaces = data?.workspaces ?? [];

  if (!isPortfolio && merchantId) {
    const merchant =
      data?.merchants?.find((m) => m.id === merchantId) ??
      workspaces.flatMap((w) => w.merchants).find((m) => m.id === merchantId);
    if (merchant) return { kind: "logo", logoUrl: merchant.logoUrl ?? null, name: merchant.name };
  }

  const effectiveWorkspaceId = workspaceId ?? (isPortfolio ? (lastAccessedWorkspaceId ?? undefined) : undefined);
  if (effectiveWorkspaceId) {
    const workspace = workspaces.find((w) => w.id === effectiveWorkspaceId);
    if (workspace) return { kind: "logo", logoUrl: workspace.logoUrl, name: workspace.name };
  }

  if (isPortfolio) return { kind: "portfolio" };

  return { kind: "logo", logoUrl: null, name: "" };
}
